import React, {useEffect, useState} from 'react';
import PropTypes from "prop-types";
import {useDispatch} from "react-redux";

import MindsetsJsSDK, {ErrorMessageAction, LoadDistrictAction} from "../../index";
import Autocomplete from "./Autocomplete";
import SchoolDropdownItem from "./SchoolDropdownItem";
import DistrictDropdown from "./DistrictDropdown";
import {EXPRESSION_EQUAL, EXPRESSION_CONTAINS} from "../../constants/filter";
import {COUNTRIES, COUNTRIES_WITH_DISTRICTS} from "../../constants/countries";
import {STATES} from "../../constants/states";

const SchoolDropdownExtended = function (
    {
        default_value,
        container_class,
        input_class,
        country,
        state,
        district_id,
        label,
        label_class,
        renderAdd,
        onSelect,
        id,
        onReset,
        with_district = false,
        placeholder = 'School name',
        disabled = false,
        persist_value = true,
        match_dropdown_width = false,
        limit = 25,
        keep_open_if_not_selected = false
    }
) {
    const dispatch = useDispatch();
    const [district, setDistrict] = useState(null);
    const [country_code, setCountryCode] = useState(country ? country : '');
    const [state_code, setSateCode] = useState(state ? state : '');

    function reset() {
        setDistrict(null);
        setCountryCode('');
        setSateCode('');
        if (typeof onReset === 'function') {
            onReset();
        }
    }

    function hasReset() {
        if (typeof onReset === 'function' && (!!district || !!country_code || !!state_code)) {
            return reset;
        }

        return null;
    }

    useEffect(() => {
        if (district_id) {
            dispatch(LoadDistrictAction(district_id)).then(district => setDistrict(district));
        }
    }, []);

    function renderExtension() {
        return (
            <div>
                <div className='d-flex flex-column'>
                    <label htmlFor="country" className='mb-0'>Country</label>
                    <select
                        id="country"
                        value={country_code}
                        onChange={event => setCountryCode(event.target.value)}
                    >
                        <option value=''>SELECT COUNTRY</option>
                        {Object.keys(COUNTRIES).map(country_code => {
                            return (
                                <option
                                    key={country_code}
                                    value={country_code}
                                >{COUNTRIES[country_code]}</option>
                            )
                        })}
                    </select>
                </div>
                {STATES.hasOwnProperty(country_code) && STATES[country_code] ? (
                    <div className='d-flex flex-column'>
                        <label htmlFor="state" className='mb-0'>State</label>
                        <select
                            id="state"
                            value={state_code}
                            onChange={event => setSateCode(event.target.value)}
                        >
                            <option value=''>Select state</option>
                            {Object.keys(STATES[country_code]).map(state_code => {
                                return (
                                    <option
                                        key={state_code}
                                        value={state_code}
                                    >{STATES[country_code][state_code]}</option>
                                )
                            })}
                        </select>
                    </div>
                ) : null}
                {COUNTRIES_WITH_DISTRICTS.includes(country_code) ? (
                    <DistrictDropdown
                        onSelect={district => {
                            setDistrict(district);

                            return district.name;
                        }}
                        default_value={district ? district.name : ''}
                        country={country_code}
                        state={state_code}
                        label='School District'
                        label_class='mb-0'
                        id={id + '-district-dropdown'}/>
                ) : null}
            </div>
        );
    }

    return (
        <Autocomplete
            placeholder={placeholder}
            fetchItems={name => {
                let options = {limit}
                options['filters[name][0]'] = EXPRESSION_CONTAINS + '=' + name;
                if (district) {
                    options['filters[district_id][0]'] = EXPRESSION_EQUAL + '=' + district.id;
                }
                if (country_code) {
                    options['filters[country][0]'] = EXPRESSION_EQUAL + '=' + country_code;
                }
                if (state_code) {
                    options['filters[state][0]'] = EXPRESSION_EQUAL + '=' + state_code;
                }
                if (with_district) {
                    options['selectors[0]'] = 'district';
                }

                return MindsetsJsSDK().API.get('schools', options)
                    .then(response => response.items)
                    .catch(error => {
                        console.error(error);
                        dispatch(ErrorMessageAction('Failed loading schools'));
                    });
            }}
            onSelect={onSelect}
            label={label}
            label_class={label_class}
            renderItem={item => <SchoolDropdownItem item={item}/>}
            default_value={default_value}
            persist_value={persist_value}
            renderAdd={renderAdd}
            container_class={container_class}
            input_class={input_class}
            id={id}
            onReset={hasReset()}
            disabled={disabled}
            debounced={true}
            extension={renderExtension()}
            match_dropdown_width={match_dropdown_width}
            keep_open_if_not_selected={keep_open_if_not_selected}
            onChange={() => onSelect(null)}
        />
    );
}

SchoolDropdownExtended.propTypes = {
    default_value: PropTypes.string,
    placeholder: PropTypes.string,
    container_class: PropTypes.string,
    input_class: PropTypes.string,
    country: PropTypes.string,
    state: PropTypes.string,
    with_district: PropTypes.bool,
    district_id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
        PropTypes.element,
    ]),
    label_class: PropTypes.string,
    renderAdd: PropTypes.func,
    onSelect: PropTypes.func.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabled: PropTypes.bool,
    persist_value: PropTypes.bool,
    match_dropdown_width: PropTypes.bool,
    onReset: PropTypes.func,
    keep_open_if_not_selected: PropTypes.bool,
    limit: PropTypes.number
};

export default SchoolDropdownExtended;
